import {
    INTERNAL_ONLY_SYSTEM_METADATA_GLOBAL_ACCOUNT,
    type AnalyticsCachedSettings,
    type AnalyticsOptions,
} from 'owa-analytics-types';
import type { StartupAnalyticsInfo } from 'owa-config-types';
import { DatapointStatus, PerformanceDatapoint } from 'owa-analytics';
import { setGlobalAnalyticsListeners } from 'owa-analytics/lib/initialization/setGlobalAnalyticsListeners';
import { initializeAnalytics as initializeAnalyticsCore } from 'owa-analytics-core/lib/initializeAnalytics';
import { setCachedSettings } from 'owa-analytics-core/lib/settings/getCachedSettings';
import { processQueueAfterInitilization } from 'owa-analytics-utils';
import { getThreadName } from 'owa-thread-config';
import { trace } from 'owa-trace';

let initializationSucceeded = false;

/**
 * Each worker should register this function with comlink. The main thread is
 * responsible for calling this function to initialize analytics in the web workers.
 */
export async function initializeAnalyticsInWorker(
    analyticsOptions: AnalyticsOptions,
    startupAnalyticsInfo: StartupAnalyticsInfo,
    cachedAnalyticsSettings: AnalyticsCachedSettings
): Promise<void> {
    trace.info(` Initializing Analytics package in ${getThreadName()}.`, 'analytics');

    // Do not use the analytics from inside a worker
    analyticsOptions.useAnalyticsWorker = false;

    // Infrastructure telemetry: account store is not available in worker context
    const datapoint = new PerformanceDatapoint(
        'AnalyticsWorkers_WorkerThread_CoreInitialized',
        INTERNAL_ONLY_SYSTEM_METADATA_GLOBAL_ACCOUNT
    );
    datapoint.addCustomData({ worker: getThreadName() });

    try {
        // Set cached analytics settings
        setCachedSettings(cachedAnalyticsSettings);

        analyticsOptions = await initializeAnalyticsCore(analyticsOptions, startupAnalyticsInfo);

        initializationSucceeded = true;

        if (!analyticsOptions.reInitializeAnalytics) {
            trace.info(` Analytics Initialized on ${getThreadName()}.`, 'analytics');

            // Setup global listeners and error handlers - Only call this once!
            setGlobalAnalyticsListeners(analyticsOptions);

            // Process all events captured since the start of the app
            processQueueAfterInitilization();

            trace.info(` Completed Analytics Initialization on ${getThreadName()}.`, 'analytics');
        } else {
            trace.info(
                ` Skipped setting global listeners and processing queue because analytics are already initialized on ${getThreadName()}.`,
                'analytics'
            );
        }

        datapoint.end();
    } catch (e) {
        if (!initializationSucceeded) {
            // If the analytics initialization failed in the worker, we need to report it to the main thread.
            // The main thread will then log the error.
            datapoint.endWithError(DatapointStatus.ServerError, e as Error);
            throw e;
        }

        datapoint.endWithError(DatapointStatus.ClientError, e as Error);

        trace.warn(
            ` The Analytics initialization failed on the ${getThreadName()} thread.`,
            'analytics'
        );
    }
}
